import { Container, Service } from 'typedi';
import { LoggerProvider } from '../logger';
import {
    AppIdentifiers,
    SDKIdentifiers,
    IntegrationMethod,
    OpeningMethod,
    SupportedLanguages,
    UserMode,
    ActionName,
    ILogger,
} from '@sparkware/uc-sdk-core';
import {
    mapAppType,
    mapApplicationMode,
    mapExtras,
    mapIntegrationMethod,
    mapOpeningMethod,
    mapOpeningMode,
    mapTranslations,
    mapUserMode,
} from './app-catalog-mappings';
import AppCatalogApi, { IGetAppCatalogResponse } from '../../../APIs/AppCatalogApi';
import { IAppCatalogEnrichedItem } from './IAppCatalogEnrichedItem';
import {
    IAppsNavigationData,
    IApp,
    ISDK,
    IAppCatalogData,
    ISupportedLanguages,
    IAppCatalog,
    IAppsCatalogData,
} from './interfaces';
import { IApplicationsDataType } from '../native-mb-adapter/models/IApplicationsData';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
    Level,
} from '@unified-client/event-formatter';
import { ClickStreamTrackingProvider } from '../tracking';
import { Utils } from '../utils';
import { MarketingBrand, RegulationType } from '../../models/enums/general-enums';
import { IElasticEventData } from '../tracking/models/interfaces/IElasticEventData';
import { WindowToken } from '../../injection-tokens';

@Service()
export class AppCatalog implements IAppCatalog {
    private _activeRegulationTypeId: number = null;
    private appsNavigationData: Record<string, IAppsNavigationData> = {};
    private _appsCatalog: IAppsCatalogData;
    private WIDGET_INIT_DEFAULT_TIMEOUT = 25;
    private TIME_IN_MILLISECONDS = 1000;
    private readonly _logger: ILogger;
    private readonly _window: Window;

    private readonly _eventFormatterBuilderFactory: EventFormatterBuilderFactory;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _utils: Utils;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('AppCatalog');
        this._window = Container.get(WindowToken);
        this._eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            this._eventFormatterBuilderFactory.createEventFormatterBuilder('AppCatalog');
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._utils = Container.get(Utils);
    }

    public getAppsCatalog = async (): Promise<IAppsCatalogData | null> => {
        try {
            let { regulationTypeId } = this._window.pageContextManager.getRegulationData();
            regulationTypeId = this._overrideRegulationType(regulationTypeId);

            if (this._activeRegulationTypeId !== regulationTypeId || !this._appsCatalog) {
                this._logger.debug(
                    `[getAppsCatalog] getAppsCatalog with regulation Id: ${regulationTypeId}, current regulation Id: ${this._activeRegulationTypeId}`,
                );
                this._activeRegulationTypeId = regulationTypeId;
                this._appsCatalog = await this._fetchCatalogData(regulationTypeId);
                if (this._appsCatalog) {
                    const { langId } = this._window.pageContextManager.getLocalizationData();
                    await this._buildAppsNavigationData(this._appsCatalog, langId);
                } else {
                    this._logger.warn(
                        `[getAppsCatalog] Could not fetch Apps Catalog with regulation Id: ${regulationTypeId}`,
                    );
                    return null;
                }
            }
            return this._appsCatalog;
        } catch (err) {
            this._logger.error(`[getAppsCatalog] failed`, err);
            return null;
        }
    };

    public getSDKs = async (userMode: UserMode): Promise<ISDK[]> => {
        const appsCatalog = await this.getAppsCatalog();
        if (!appsCatalog) return null;
        const { sdks } = appsCatalog;
        return Object.values(
            userMode === UserMode.Anonymous
                ? await this.filterBy(appsCatalog, 'userMode', userMode)
                : sdks,
        ).filter((sdk: ISDK) => !!sdk);
    };

    public getAppCatalogData = async <T = null>(appId: string): Promise<IAppCatalogData<T>> => {
        try {
            this._logger.debug(`[getAppCatalogData] start`);
            const appsCatalog = await this.getAppsCatalog();
            if (!appsCatalog) return null;

            const { apps, sdks } = appsCatalog;
            const app = apps[appId];
            if (app === undefined) {
                this._logger.error(`No app found for appId: ${appId}`);
                return null;
            }
            const sdk = sdks[app.sdkName];
            if (sdk === undefined && app.openingMethod !== OpeningMethod.GNP) {
                this._logger.error(`No sdk found for appId: ${appId}`);
                return null;
            }
            return {
                app,
                sdk,
            };
        } catch (err) {
            this._logger.error(`[getAppCatalogData] failed`, err);
            return null;
        }
    };

    public async getMiniAppIdByAppID(appId: string): Promise<AppIdentifiers> {
        const searchedApp: IAppCatalogData = await this.getAppCatalogData(appId);
        if (searchedApp === undefined) {
            throw new Error(`No app found for appId: ${appId}`);
        }
        const { miniAppID } = searchedApp.app;
        if (!miniAppID) {
            throw new Error(`No Mini App Id found inside appId: ${appId}`);
        }
        return miniAppID;
    }

    public getRestrictedApps = async (RestrictionsOffering: number[]): Promise<string[]> => {
        const appsCatalog = await this.getAppsCatalog();
        if (!appsCatalog) return null;
        const { apps } = appsCatalog;
        let RestrictedApps: string[] = [];
        RestrictionsOffering?.forEach((OfferingID) => {
            Object.values(apps).forEach((app) => {
                if (app.offeringID == OfferingID) {
                    RestrictedApps.push(app.ID);
                }
            });
        });
        return RestrictedApps;
    };

    public hasAppID = async (appID: string): Promise<boolean> => {
        const appsCatalog = await this.getAppsCatalog();
        if (!appsCatalog) return null;
        const { apps } = appsCatalog;
        return Boolean(apps[appID]);
    };

    public async getAppNavigationData(url: string): Promise<IAppsNavigationData> {
        return this.appsNavigationData[url];
    }

    public getAppIdByProductPackage = async (productPackage: string): Promise<string> => {
        const appsCatalog = await this.getAppsCatalog();
        if (!appsCatalog) return null;
        const { apps } = appsCatalog;
        return Object.values(apps).find(
            (app) => app.productPackageToID?.toString() == productPackage,
        )?.ID;
    };

    public async filterAppsCatalogData(): Promise<IApplicationsDataType> {
        const appsCatalog = await this.getAppsCatalog();
        if (!appsCatalog) return null;
        const { apps } = appsCatalog;
        if (!apps) return null;
        const filteredObj = {};
        for (const key in apps) {
            if (apps.hasOwnProperty(key)) {
                const initTimeoutSeconds =
                    apps[key].initTimeout || this.WIDGET_INIT_DEFAULT_TIMEOUT;
                filteredObj[key] = {
                    appID: apps[key].ID,
                    initTimeout: initTimeoutSeconds,
                };
            }
        }
        return filteredObj;
    }

    public async getWidgetInitTimeout(appID: AppIdentifiers): Promise<number> {
        const { app } = await this.getAppCatalogData(appID);
        const initTimeoutSeconds = app.initTimeout || this.WIDGET_INIT_DEFAULT_TIMEOUT;
        return initTimeoutSeconds * this.TIME_IN_MILLISECONDS;
    }

    //TODO remove when NTS will align SDKs
    private _overrideRegulationType(regulationTypeId: number): number {
        let { marketingBrandId } = this._window.pageContextManager.getBrandData();
        const overridableRegulationTypes = new Set([RegulationType.MGA]);

        if (
            overridableRegulationTypes.has(regulationTypeId) &&
            marketingBrandId !== MarketingBrand.MrGreen
        ) {
            return RegulationType.All;
        }

        return regulationTypeId;
    }

    private _fetchCatalogData = async (regulationId: number): Promise<IAppsCatalogData> => {
        try {
            this._logger.debug(`[_fetchCatalogData] start`);
            const { response, errorResponse } = await AppCatalogApi.GetAppCatalog({
                regulationId,
            });
            let appCatalogData: IAppsCatalogData | Promise<IAppsCatalogData>;
            if (response) {
                appCatalogData = await this._mapAppsCatalogData(response);
            } else {
                this._logger.error(
                    "AppCatalog - couldn't fetch data.\nError Response:",
                    errorResponse,
                );
                appCatalogData = null;
            }
            return appCatalogData;
        } catch (err) {
            this._logger.error(`[_fetchCatalogData] failed`, err);
            const formatter = this._eventFormatterBuilder.createFormatter('_fetchCatalogData');
            const event = formatter.formatUCEvent(
                { message: 'Error fetching app catalog' },
                { correlationID: this._utils.generateCorrelationID(), level: Level.critical },
                { event: 'fetchAppCatalog' },
            );
            this._clickStreamTrackingProvider.sendEventV2(event);
            return null;
        }
    };

    private async _mapAppsCatalogData(response: IGetAppCatalogResponse): Promise<IAppsCatalogData> {
        try {
            const apps = this.mapAppsApiResponseToApps(response.Catalog);
            const sdks = this.mapAppsApiResponseToSDKs(response.Catalog);
            this._logger.debug(`Data from BFF, Apps: ${JSON.stringify({ apps, sdks })}`);
            return {
                apps,
                sdks,
            };
        } catch (err) {
            this._logger.error("AppCatalog - couldn't map fetched data", err);
            return null;
        }
    }

    private _buildAppsNavigationData = async (
        appsCatalog: IAppsCatalogData,
        lang: string,
    ): Promise<void> => {
        this._logger.debug('Building apps navigation data');
        if (!appsCatalog) return null;
        const { apps } = appsCatalog;
        const appsWithTranslations = Object.values(apps).filter((app) => !!app?.translations);
        for (let app of appsWithTranslations) {
            let translations: ISupportedLanguages =
                app?.translations[lang] || app?.translations[SupportedLanguages.EN];
            if (!!translations?.pageURL) {
                const pageUrlKey = `/app${translations.pageURL}`;
                this.appsNavigationData[pageUrlKey] = {
                    actionID: app.actionID,
                    url: translations?.pageURL,
                    pageTitle: translations?.pageTitle,
                    appID: app.ID,
                };
            }
        }
        this._logger.debug('Apps navigation data has been built successfully');
    };

    private filterBy = async <K extends keyof IApp>(
        appsCatalog: IAppsCatalogData,
        field: K,
        value: IApp[K],
    ): Promise<Record<string, ISDK>> => {
        const { apps, sdks } = appsCatalog;
        return Object.keys(apps).reduce((acc, key) => {
            if (apps[key][field] === value) {
                const sdkKey = apps[key].sdkName;
                if (sdkKey && acc[sdkKey] === undefined) {
                    acc[sdkKey] = sdks[sdkKey];
                }
            }
            return acc;
        }, {});
    };

    private mapAppsApiResponseToSDKs = (
        catalog: Record<string, IAppCatalogEnrichedItem>,
    ): Record<string, ISDK> => {
        return Object.keys(catalog).reduce((sdks, appId) => {
            const {
                Endpoint,
                SdkName,
                InstanceName,
                AppVersion,
                OpeningMethod: openingMethod,
            } = catalog[appId];

            let sdkVersion = AppVersion;

            if (mapOpeningMethod(openingMethod) === OpeningMethod.GNP) {
                return sdks;
            }

            const existing = sdks[SdkName];
            if (!!existing) {
                if (existing.endpoint !== Endpoint) {
                    this._logger.error(
                        `Endpoint mismatch for SDK ${SdkName}: expected ${existing.endpoint}, but got ${Endpoint}. Skipping this SDK registration.`,
                    );
                    sdks[SdkName] = {
                        endpoint: '', // we don't know which endpoint is the correct one, so we return empty string
                        sdkName: SdkName,
                        objectName: InstanceName,
                        version: sdkVersion,
                    };
                    const formatter = this._eventFormatterBuilder.createFormatter(
                        'mapAppsApiResponseToSDKs',
                    );
                    const eventData: IElasticEventData = {
                        event: 'mapAppsApiResponseToSDKs',
                        dataString: JSON.stringify({
                            existingEndpoint: existing.endpoint,
                            newEndpoint: Endpoint,
                            SdkName,
                        }),
                    };
                    this._clickStreamTrackingProvider.sendElasticEvent(
                        formatter,
                        'Fetch App Catalog - error - endpoint mismatch',
                        eventData,
                        null,
                        this._utils.generateCorrelationID(),
                        Level.critical,
                    );

                    return sdks; // Skip the current SDK and continue with the next
                }

                if (existing.version !== AppVersion) {
                    sdkVersion = Math.max(AppVersion, existing.version);
                    this._logger.warn(
                        `Mismatch on "${SdkName}" SDK versions ${existing.version} and ${AppVersion}. Taking ${sdkVersion} (max) for optimistic cache-busting policy`,
                    );
                }

                if (existing.objectName !== InstanceName) {
                    this._logger.warn(
                        `Same SDK is registered with multiple instance names: ${existing.objectName}, ${InstanceName}`,
                    );
                }
            }

            sdks[SdkName] = {
                endpoint: Endpoint,
                sdkName: SdkName,
                objectName: InstanceName,
                version: sdkVersion,
            };

            return sdks;
        }, {});
    };

    private mapAppsApiResponseToApps = (
        catalog: Record<string, IAppCatalogEnrichedItem>,
    ): Record<string, IApp> => {
        return Object.keys(catalog).reduce((apps, key) => {
            const fullApp = catalog[key];

            apps[key] = {
                ID: fullApp.AppId,
                actionID: fullApp.ActionId as ActionName,
                appType: mapAppType(fullApp.AppType),
                isIframe:
                    mapIntegrationMethod(fullApp.IntegrationMethod) === IntegrationMethod.IFrame,
                level: fullApp.Level,
                minimumCapabilityRequirements: fullApp.MinimumCapabilityRequirements,
                offeringID: fullApp.OfferingId,
                openingMethod: mapOpeningMethod(fullApp.OpeningMethod),
                productPackageToID: fullApp.ProductPackageToId,
                regulations: fullApp.Regulations,
                translations: mapTranslations(fullApp.Translations),
                userMode: mapUserMode(fullApp.SupportedUserModes),
                supportedApplicationModes:
                    fullApp.SupportedApplicationModes.map(mapApplicationMode),
                version: fullApp.AppVersion,
                openingMode:
                    fullApp.OpeningMode != undefined
                        ? mapOpeningMode(fullApp.OpeningMode)
                        : undefined,
                sdkName: fullApp.SdkName as SDKIdentifiers,
                title: fullApp.Title,
                extras: mapExtras(fullApp.Extras),
                scriptType: fullApp.ScriptType,
                miniAppID: fullApp.MiniAppID,
                initTimeout: fullApp.InitTimeout,
            };

            return apps;
        }, {});
    };
}
